import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { setCanonical, setMeta } from '@/utils/seo';
import { SpeakableSchema, BreadcrumbSchema } from '@/components/SchemaMarkup';
import { MapPin, Search, Loader2, Plus, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import HeroBackground from '@/components/HeroBackground';
import api from '@/utils/api';

const TOP_CITIES = [
  { name: 'Los Angeles', slug: 'los-angeles', county: 'Los Angeles County' },
  { name: 'San Francisco', slug: 'san-francisco', county: 'San Francisco County' },
  { name: 'San Diego', slug: 'san-diego', county: 'San Diego County' },
  { name: 'San Jose', slug: 'san-jose', county: 'Santa Clara County' },
  { name: 'Sacramento', slug: 'sacramento', county: 'Sacramento County' },
  { name: 'Fresno', slug: 'fresno', county: 'Fresno County' },
  { name: 'Oakland', slug: 'oakland', county: 'Alameda County' },
  { name: 'Long Beach', slug: 'long-beach', county: 'Los Angeles County' },
  { name: 'Bakersfield', slug: 'bakersfield', county: 'Kern County' },
  { name: 'Anaheim', slug: 'anaheim', county: 'Orange County' },
  { name: 'Riverside', slug: 'riverside', county: 'Riverside County' },
  { name: 'Santa Clarita', slug: 'santa-clarita', county: 'Los Angeles County' },
];

const INDUSTRY_SLUGS = [
  { label: 'Construction', slug: 'construction' },
  { label: 'Restaurants', slug: 'restaurants' },
  { label: 'Home Health', slug: 'home-health' },
  { label: 'Landscaping', slug: 'landscaping' },
  { label: 'Trucking', slug: 'trucking' },
  { label: 'All', slug: 'all-businesses' },
];

export default function CitiesPage() {
  const [cities, setCities] = useState([]);
  const [search, setSearch] = useState('');
  const [customCity, setCustomCity] = useState('');
  const [creating, setCreating] = useState(false);
  const [createMsg, setCreateMsg] = useState('');
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setCanonical('/cities');
    setMeta('Find Workers Comp Insurance in Your California City | CompareWorkersComp.com',
      'Find workers compensation insurance in your California city. Search your city and compare quotes from 50+ carriers.');
    api.get('/cities?limit=500').then(r => setCities(r.data.cities || [])).catch(() => {});
  }, []);

  const filtered = search
    ? cities.filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
    : [];

  const handleCityCreate = async (cityName) => {
    const name = cityName || customCity.trim();
    if (!name) return;
    setCreating(true);
    setCreateMsg('');
    try {
      const { data } = await api.post('/cities/lookup', { name });
      if (data.city) {
        setCreateMsg(data.created ? `Created! Redirecting to ${data.city.name}...` : `Found! Redirecting...`);
        setTimeout(() => navigate(`/workers-comp-insurance-${data.city.slug}-ca`), 800);
      }
    } catch {
      setCreateMsg('Could not find or create city. Please try again.');
    }
    setCreating(false);
  };

  return (
    <>
      <SpeakableSchema name="Find Workers Comp in Your California City" url="https://compareworkerscomp.com/cities" />
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'Find Your City' }]} />

      {/* Hero with Search + Can't Find */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <HeroBackground />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="speakable-intro text-white/30 text-xs mb-3">Search any California city to find workers compensation insurance quotes, industry-specific coverage, and licensed agents in your area.</p>
          <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-3 hero-text-shadow" data-testid="cities-h1">Find Workers Comp in Your <span className="text-red-500">California City</span></h1>
          <p className="text-white/60 mb-8">Search any California city or type a new one — we'll build a page for it instantly</p>

          {/* Search bar */}
          <div className="max-w-lg mx-auto relative" data-testid="city-search">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search your city..."
              value={search}
              onChange={e => { setSearch(e.target.value); setCustomCity(e.target.value); setCreateMsg(''); }}
              onKeyDown={e => e.key === 'Enter' && filtered.length === 0 && search.length > 1 && handleCityCreate(search)}
              className="w-full pl-12 pr-4 py-4 rounded-xl text-base border-0 shadow-lg focus:ring-2 focus:ring-red-400 outline-none"
              data-testid="city-search-input"
            />
          </div>

          {/* Search results dropdown */}
          {search && filtered.length > 0 && (
            <div className="max-w-lg mx-auto mt-2 bg-white rounded-xl shadow-lg max-h-64 overflow-y-auto text-left" data-testid="city-search-results">
              {filtered.slice(0, 10).map(c => (
                <Link key={c.slug} to={`/workers-comp-insurance-${c.slug}-ca`} className="flex items-center gap-2 px-4 py-3 hover:bg-red-50 border-b border-gray-50 transition-colors">
                  <MapPin className="w-4 h-4 text-red-500 shrink-0" />
                  <span className="text-cwc-navy font-medium text-sm">{c.name}, CA</span>
                </Link>
              ))}
            </div>
          )}

          {/* Can't find — shows when search has no results */}
          {search && search.length > 1 && filtered.length === 0 && (
            <div className="max-w-lg mx-auto mt-4 bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20" data-testid="cant-find-inline">
              <div className="flex items-center gap-2 mb-3">
                <Plus className="w-5 h-5 text-yellow-400" />
                <p className="text-white font-semibold text-sm">Don't see your city? We'll create it!</p>
              </div>
              <button
                onClick={() => handleCityCreate(search)}
                disabled={creating}
                className="bg-red-600 text-white px-6 py-2.5 rounded-lg text-sm font-bold hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 mx-auto transition-colors"
                data-testid="create-city-btn"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Create Page for "{search}"
              </button>
              {createMsg && <p className="text-sm mt-3 text-green-400 font-medium">{createMsg}</p>}
            </div>
          )}

          {/* Always-visible "Can't find" hint when not searching */}
          {!search && (
            <div className="mt-5">
              <button
                onClick={() => document.querySelector('[data-testid="city-search-input"]').focus()}
                className="inline-flex items-center gap-2 bg-green-600 text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-green-700 transition-colors"
                data-testid="cant-find-hint-btn"
              >
                <Plus className="w-4 h-4" /> Can't find your city? Type it — we'll build it!
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Top 12 Cities — nice cards with industry links */}
      <section className="bg-white py-16" data-testid="popular-cities">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-cwc-navy mb-3">Popular California Cities</h2>
          <p className="text-gray-500 mb-10">Workers comp coverage for California's largest metro areas</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {TOP_CITIES.map(city => (
              <div key={city.slug} className="bg-gray-50 rounded-xl p-5 border border-gray-100 hover:border-red-200 hover:shadow-md transition-all group" data-testid={`popular-city-${city.slug}`}>
                <Link to={`/workers-comp-insurance-${city.slug}-ca`} className="flex items-center gap-2 mb-1">
                  <MapPin className="w-4 h-4 text-red-500" />
                  <h3 className="font-bold text-cwc-navy text-lg group-hover:text-red-600 transition-colors">{city.name}, CA</h3>
                </Link>
                <p className="text-gray-400 text-xs mb-3 ml-6">{city.county}</p>
                <div className="flex flex-wrap gap-1.5 ml-6">
                  {INDUSTRY_SLUGS.map(ind => (
                    <Link key={ind.slug} to={`/workers-comp-for-${ind.slug}-in-${city.slug}-ca`} className="text-[10px] bg-white text-gray-500 px-2 py-1 rounded-md border border-gray-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors">
                      {ind.label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Browse All Cities — collapsible */}
      <section className="bg-gray-50 py-12" data-testid="all-cities-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setShowAll(!showAll)}
            className="flex items-center gap-3 w-full text-left"
            data-testid="toggle-all-cities"
          >
            <h2 className="text-xl font-bold text-cwc-navy">Browse All {cities.length} California Cities</h2>
            {showAll ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>
          {showAll && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-1.5 mt-6 animate-fadeInUp" data-testid="all-cities-list">
              {cities.map(c => (
                <Link key={c.slug} to={`/workers-comp-insurance-${c.slug}-ca`} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-red-600 px-2 py-1.5 rounded hover:bg-white transition-colors">
                  <MapPin className="w-3 h-3 shrink-0 text-red-400" /> {c.name}
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-cwc-navy py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Don't See Your City?</h2>
          <p className="text-white/60 mb-6">Just type any California city in the search bar above — we'll instantly create a workers comp comparison page for your area. Our site grows automatically to serve every community.</p>
          <a href="#city-search" className="inline-flex items-center gap-2 bg-red-600 text-white font-bold px-6 py-3 rounded-full hover:bg-red-700 transition-colors" data-testid="cities-cta">
            <Search className="w-4 h-4" /> Search Your City <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Carriers */}
      <section className="bg-gray-50 py-10">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-xs uppercase tracking-widest text-gray-400 font-medium mb-3">Our Partners — Trusted Insurance Carriers</p>
          <img src="/carriers.png" alt="Insurance Carriers" className="max-w-full mx-auto opacity-80" loading="lazy" />
        </div>
      </section>
    </>
  );
}
